import moment from 'moment-timezone';
import {
  format,
  subDays,
  addDays,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfWeek,
  startOfQuarter,
  endOfQuarter,
  subMonths,
  subYears,
} from 'date-fns';

import { getLoginUserId } from '../../../../utils/Helpers';
import { DATE_OPERATORS } from '../../../../constants';

export const getChartQuery = params => {
  const where = [];
  const segments = [];
  const timeDimensions = [];
  let ignoreCondition = false;
  const quickFilters =
    params?.custom_properties?.quick_filter_chart_conditions || [];
  const filters =
    params?.chart_filter_conditions &&
    params?.chart_filter_conditions?.filters?.length
      ? params?.chart_filter_conditions?.filters
      : [];
  filters.forEach(item => {
    const innerCondition = [];
    item?.fields?.forEach(innerRecord => {
      if (!innerRecord.tableField) {
        ignoreCondition = true;
      }
      let operator = innerRecord.operator;

      if (innerRecord.isTimeFilter && !innerRecord.operatorType) {
        calculateDateRange(innerRecord, innerCondition);
      } else if (
        innerRecord.isTimeFilter &&
        innerRecord.operatorType == 'daysField' &&
        innerRecord.operator != 'custom'
      ) {
        calculateDateRange(innerRecord, innerCondition);
      } else if (
        innerRecord.isTimeFilter &&
        innerRecord.operatorType == 'monthWeekField' &&
        !innerRecord.value
      ) {
        calculateDateRange(innerRecord, innerCondition);
      } else if (
        innerRecord.isTimeFilter &&
        innerRecord.operatorType == 'monthWeekField' &&
        innerRecord.value
      ) {
        calculateMonthWeekDateRange(innerRecord, innerCondition);
      } else if (
        innerRecord.isTimeFilter &&
        innerRecord.operatorType == 'datePicker'
      ) {
        operator = innerRecord.dateFilterOperatorType;
        const isDateRange = innerRecord.operator == 'inDateRange';
        if (isDateRange) {
          innerCondition.push({
            member: innerRecord.tableField,
            values: [innerRecord.value, innerRecord.to_date_value],
            operator: innerRecord.operator,
          });
        } else {
          innerCondition.push({
            member: innerRecord.tableField,
            values: [innerRecord.value],
            operator: operator,
          });
        }
      } else if (innerRecord.operatorType == 'nullField') {
        innerCondition.push({
          member: innerRecord.tableField,
          values: [null],
          operator: innerRecord.dateFilterOperatorType,
        });
      } else {
        innerCondition.push({
          member: innerRecord.tableField,
          values: [innerRecord.value.toString()],
          operator: operator,
        });
      }
    });

    const finalCondition = [...innerCondition];

    where.push({
      [item.condition]: finalCondition,
    });
  });
  const SYMMETRIC_OPERATORS = ['eq', 'ne'];

  const OPERATOR_FLIP_MAP = {
    gt: 'lt',
    lt: 'gt',
    gte: 'lte',
    lte: 'gte',
  };

  quickFilters.forEach(filter => {
    if (filter.compareWithField && filter.tableField && filter.operator) {
      let left = filter.tableField.split('.')?.[1];
      let right = filter.compareWithField.split('.')?.[1];
      let operator = filter.operator?.value;

      if (!right) return;

      if (SYMMETRIC_OPERATORS.includes(operator)) {
        [left, right] = [left, right].sort();
      } else if (OPERATOR_FLIP_MAP[operator]) {
        if (left > right) {
          [left, right] = [right, left];

          operator = OPERATOR_FLIP_MAP[operator];
        }
      }

      const segmentKey = `${filter.tableField.split('.')?.[0]}.${left}_${operator}_${right}`;
      segments.push(segmentKey);
    }
  });
  const groupByDimension =
    params?.chart_configuration?.groupBy?.map(
      groupByRecord => groupByRecord.name
    ) || [];

  // let dimensions =
  // params?.chart_configuration?.dimension && !params?.chart_configuration?.isPrimaryKeySelected
  //   ? [params.chart_configuration.dimension]
  //   : groupByDimension;

  let dimensions = groupByDimension;
  let measures = params?.chart_configuration?.measureSummarizedBy
    ? [params.chart_configuration.measureSummarizedBy]
    : [];

  const orderBy =
    params?.chart_configuration?.orderByField &&
    params?.chart_configuration?.orderBy
      ? [
          [
            params.chart_configuration.orderByField,
            params.chart_configuration.orderBy,
          ],
        ]
      : [];
  const query = {
    // limit: params.tableLimit || params?.chart_configuration?.measureTarget,
    measures: measures,
    dimensions: dimensions,
    timeDimensions: timeDimensions,
    order: orderBy,
    filters: ignoreCondition
      ? []
      : [{ [params.chart_filter_conditions.condition]: [...where] }],
    ...(segments && segments.length > 0 && { segments }),
  };

  return query;
};

export const getCompareToQuery = params => {
  const where = [];
  const timeDimensions = [];
  let ignoreCondition = false;

  const compareField = params?.custom_properties?.compareField;

  const filters = params?.chart_filter_conditions?.filters?.length
    ? params.chart_filter_conditions.filters
    : [];

  filters.forEach(item => {
    const innerCondition = [];

    item?.fields?.forEach(innerRecord => {
      if (
        compareField &&
        innerRecord.isTimeFilter &&
        innerRecord.tableField === compareField
      ) {
        return;
      }

      if (!innerRecord.tableField) {
        ignoreCondition = true;
      }

      let operator = innerRecord.operator;

      if (!innerRecord.tableField) return;

      if (innerRecord.isTimeFilter && !innerRecord.operatorType) {
        calculateDateRange(innerRecord, innerCondition);
      } else if (
        innerRecord.isTimeFilter &&
        innerRecord.operatorType === 'daysField' &&
        innerRecord.operator !== 'custom'
      ) {
        calculateDateRange(innerRecord, innerCondition);
      } else if (
        innerRecord.isTimeFilter &&
        innerRecord.operatorType === 'datePicker'
      ) {
        operator = innerRecord.dateFilterOperatorType;

        if (innerRecord.operator === 'inDateRange') {
          innerCondition.push({
            member: innerRecord.tableField,
            values: [innerRecord.value, innerRecord.to_date_value],
            operator: innerRecord.operator,
          });
        } else {
          innerCondition.push({
            member: innerRecord.tableField,
            values: [innerRecord.value],
            operator,
          });
        }
      } else if (innerRecord.operatorType === 'nullField') {
        innerCondition.push({
          member: innerRecord.tableField,
          values: [null],
          operator: innerRecord.dateFilterOperatorType,
        });
      } else {
        innerCondition.push({
          member: innerRecord.tableField,
          values: [innerRecord.value.toString()],
          operator,
        });
      }
    });

    if (innerCondition.length) {
      where.push({
        [item.condition]: innerCondition,
      });
    }
  });

  if (compareField) {
    timeDimensions.push({
      dimension: compareField,
      dateRange: params?.custom_properties?.compareOperator?.key,
    });
  }

  const query = {
    measures: params?.chart_configuration?.measureSummarizedBy
      ? [params.chart_configuration.measureSummarizedBy]
      : [],
    dimensions: params?.chart_configuration?.groupBy?.map(g => g.name) || [],
    timeDimensions,
    order:
      params?.chart_configuration?.orderByField &&
      params?.chart_configuration?.orderBy
        ? [
            [
              params.chart_configuration.orderByField,
              params.chart_configuration.orderBy,
            ],
          ]
        : [],
    filters: ignoreCondition
      ? []
      : where.length
        ? [{ [params.chart_filter_conditions.condition]: where }]
        : [],
  };

  return query;
};

export const getTableQuery = params => {
  const where = [];
  const timeDimensions = [];
  const measures = [];
  const segments = [];
  let ignoreCondition = false;
  const quickFilters =
    params?.custom_properties?.quick_filter_report_conditions || [];
  params?.table_filter_conditions?.filters?.forEach(item => {
    const innerCondition = [];
    item.fields.forEach(innerRecord => {
      if (!innerRecord.tableField) {
        ignoreCondition = true;
      }
      let operator = innerRecord.operator;

      if (innerRecord.isTimeFilter && !innerRecord.operatorType) {
        calculateDateRange(innerRecord, innerCondition);
      } else if (
        innerRecord.isTimeFilter &&
        innerRecord.operatorType == 'daysField' &&
        innerRecord.operator != 'custom'
      ) {
        calculateDateRange(innerRecord, innerCondition);
      } else if (
        innerRecord.isTimeFilter &&
        innerRecord.operatorType == 'monthWeekField' &&
        !innerRecord.value
      ) {
        calculateDateRange(innerRecord, innerCondition);
      } else if (
        innerRecord.isTimeFilter &&
        innerRecord.operatorType == 'monthWeekField' &&
        innerRecord.value
      ) {
        calculateMonthWeekDateRange(innerRecord, innerCondition);
      } else if (
        innerRecord.isTimeFilter &&
        innerRecord.operatorType == 'datePicker'
      ) {
        operator = innerRecord.dateFilterOperatorType;
        const isDateRange = innerRecord.operator == 'inDateRange';
        if (isDateRange) {
          innerCondition.push({
            member: innerRecord.tableField,
            values: [innerRecord.value, innerRecord.to_date_value],
            operator: innerRecord.operator,
          });
        } else {
          innerCondition.push({
            member: innerRecord.tableField,
            values: [innerRecord.value],
            operator: operator,
          });
        }
      } else if (innerRecord.operatorType == 'nullField') {
        innerCondition.push({
          member: innerRecord.tableField,
          values: [null],
          operator: innerRecord.dateFilterOperatorType,
        });
      } else {
        innerCondition.push({
          member: innerRecord.tableField,
          values: [innerRecord.value.toString()],
          operator: operator,
        });
      }
    });
    const finalCondition = [...innerCondition];
    where.push({
      [item.condition]: finalCondition,
    });
  });
  let dimensionsList =
    params?.table_configuration?.map(item => item.measureName) || [];
  if (params?.report_group_by) {
    dimensionsList = [params.report_group_by.dimension];
    if (params?.report_group_by?.primaryKeyMeasureName) {
      measures.push(params?.report_group_by?.primaryKeyMeasureName);
    }
    /* if (params?.report_group_by_aggr && Object.entries(params?.report_group_by_aggr).length) {
      for (const key in params?.report_group_by_aggr) {
        if (params?.report_group_by_aggr[key]?.measureSummarizedBy)
          measures.push(params?.report_group_by_aggr[key].measureSummarizedBy);
      }
    } */
  }
  if (Array.isArray(params?.custom_properties?.additional_dimensions)) {
    dimensionsList = [
      ...dimensionsList,
      ...(params?.custom_properties?.additional_dimensions || []),
    ];
    params?.custom_properties?.additional_dimensions.forEach(element => {
      where.push({
        member: element,
        operator: 'set',
      });
    });
  }
  const order = [];
  if (params?.order_by?.orderBy && params?.order_by?.orderField) {
    order.push([params?.order_by?.orderField, params?.order_by?.orderBy]);
  }
  const SYMMETRIC_OPERATORS = ['eq', 'ne'];

  const OPERATOR_FLIP_MAP = {
    gt: 'lt',
    lt: 'gt',
    gte: 'lte',
    lte: 'gte',
  };

  quickFilters.forEach(filter => {
    if (filter.compareWithField && filter.tableField && filter.operator) {
      let left = filter.tableField.split('.')?.[1];
      let right = filter.compareWithField.split('.')?.[1];
      let operator = filter.operator?.value;

      if (!right) return;

      if (SYMMETRIC_OPERATORS.includes(operator)) {
        [left, right] = [left, right].sort();
      } else if (OPERATOR_FLIP_MAP[operator]) {
        if (left > right) {
          [left, right] = [right, left];

          operator = OPERATOR_FLIP_MAP[operator];
        }
      }

      const segmentKey = `${filter.tableField.split('.')?.[0]}.${left}_${operator}_${right}`;
      segments.push(segmentKey);
    }
  });

  const query = {
    measures: Array.isArray(params?.measures)
      ? [...new Set(params?.measures)]
      : measures,
    limit: params?.tableLimit,
    dimensions:
      dimensionsList && dimensionsList.length
        ? [...new Set(dimensionsList)]
        : [],
    timeDimensions: timeDimensions,
    order: order,
    filters: ignoreCondition
      ? []
      : [{ [params?.table_filter_conditions.condition]: [...where] }],
    // ungrouped: true,
    ...(segments && segments.length > 0 && { segments }),
  };
  return query;
};

export const mapResponseToFormik = res => {
  return {
    widget_type: res.widget_display_type[0],
    widget_name: res.widget_name,
    widget_description: res.widget_description,
    chart_configuration: res.chart_configuration,
    table_configuration: res.table_configuration,
    chart_filter_conditions: res.chart_filter_conditions,
    table_filter_conditions: res.table_filter_conditions,
    order_by: res.order_by,
    widget_filter_type: res.widget_filter_type,
    custom_properties: res.custom_properties,
    editable: res.editable,
    owner_user_id: res.owner_user_id,
    route_widget: res.route_widget || 0,
    report_group_by: res?.report_group_by,
    report_group_by_aggr: res?.report_group_by_aggr,
    is_third_party: res?.is_third_party || 0,
  };
};
export const prepareChartQueryWithFilters = ({
  query,
  formattedQuery,
  filterDetails,
}) => {
  widgetTypeFilter(query, formattedQuery, filterDetails);
  return formattedQuery;
};
export const prepareQueryWithFilters = ({
  tablePagination,
  query,
  formattedQuery,
  cubeResponse,
  isCountQuery = false,
  filterDetails,
  orderByConfiguration,
  paginator,
}) => {
  widgetTypeFilter(query, formattedQuery, filterDetails);
  OrderBy(orderByConfiguration, formattedQuery);
  if (!isCountQuery) {
    formattedQuery.limit = paginator.perPage;
    formattedQuery.offset = paginator.currentPage * paginator.perPage;
  }
  getColumnFilters(tablePagination, cubeResponse, formattedQuery);
  return formattedQuery;
};
function OrderBy(orderByConfiguration, formattedQuery) {
  if (orderByConfiguration?.columns && orderByConfiguration?.direction) {
    formattedQuery.order = [
      [
        orderByConfiguration?.columns,
        orderByConfiguration?.direction > 0 ? 'asc' : 'desc',
      ],
    ];
  }
}

function getInvalidWeekDateValues(operator) {
  const invalidDate = '1970-01-01';

  switch (operator) {
    case 'equals':
    case 'notEquals':
      return [invalidDate, invalidDate];
    case 'beforeDate':
    case 'afterDate':
      return [invalidDate];
    default:
      return [invalidDate, invalidDate];
  }
}

function getFirstSundayOfMonth(monthDate) {
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const dayOfWeek = monthStart.getDay();

  const firstSunday =
    dayOfWeek === 0 ? monthStart : addDays(monthStart, 7 - dayOfWeek);

  return firstSunday > monthEnd ? monthEnd : firstSunday;
}

function getMonthWeekRange(monthDate, weekNumber) {
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const weekOneEnd = getFirstSundayOfMonth(monthDate);

  if (weekNumber === 1) {
    return { rangeStart: monthStart, rangeEnd: weekOneEnd };
  }

  const rangeStart = addDays(weekOneEnd, 1 + (weekNumber - 2) * 7);
  if (rangeStart > monthEnd) return null;

  const rangeEnd = addDays(rangeStart, 6);
  return {
    rangeStart,
    rangeEnd: rangeEnd > monthEnd ? monthEnd : rangeEnd,
  };
}

function getMonthWeekCount(monthDate) {
  let weekNumber = 1;
  while (getMonthWeekRange(monthDate, weekNumber)) {
    weekNumber++;
  }
  return weekNumber - 1;
}

function resolveMonthWeekNumber(monthDate, weekValue) {
  const totalWeeks = getMonthWeekCount(monthDate);
  const weekMatch = weekValue?.match(/^Week (\d+)(?:\(if applicable\))?$/);

  if (weekMatch) {
    const weekNumber = parseInt(weekMatch[1], 10);
    if (weekNumber < 1 || weekNumber > totalWeeks) {
      return null;
    }
    return weekNumber;
  }

  if (weekValue === 'Last Week') {
    return totalWeeks > 0 ? totalWeeks : null;
  }
  if (weekValue === '2nd Last Week') {
    return totalWeeks >= 2 ? totalWeeks - 1 : null;
  }

  return null;
}

function getMonthHalfRange(monthDate, halfValue) {
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);

  if (halfValue === 'First Half Month(1-15)') {
    return {
      rangeStart: monthStart,
      rangeEnd: addDays(monthStart, 14),
    };
  }
  if (halfValue === 'Second Half Month(16-Last Day)') {
    return {
      rangeStart: addDays(monthStart, 15),
      rangeEnd: monthEnd,
    };
  }
  return null;
}

function getMonthWeekRangeBySelection(monthDate, weekValue) {
  const halfRange = getMonthHalfRange(monthDate, weekValue);
  if (halfRange) {
    return halfRange;
  }

  const weekNumber = resolveMonthWeekNumber(monthDate, weekValue);
  if (!weekNumber) {
    return null;
  }
  return getMonthWeekRange(monthDate, weekNumber);
}

function calculateMonthWeekDateRange(innerRecord, innerCondition) {
  const today = new Date();
  const operator = innerRecord.dateFilterOperatorType;
  const multiDateOperators = {
    equals: 'inDateRange',
    notEquals: 'notInDateRange',
    beforeDate: 'beforeDate',
    afterDate: 'afterDate',
  };

  let monthDate = null;
  if (innerRecord.operator === DATE_OPERATORS?.THIS_MONTH) {
    monthDate = today;
  } else if (innerRecord.operator === 'Last month') {
    monthDate = subMonths(today, 1);
  }

  if (!monthDate) return;

  const weekRange = getMonthWeekRangeBySelection(monthDate, innerRecord.value);

  let dateValues = [];
  if (!weekRange) {
    dateValues = getInvalidWeekDateValues(operator);
  } else {
    const { rangeStart, rangeEnd } = weekRange;

    switch (operator) {
      case 'equals':
      case 'notEquals':
        dateValues = [
          format(rangeStart, 'yyyy-MM-dd'),
          format(rangeEnd, 'yyyy-MM-dd'),
        ];
        break;
      case 'beforeDate':
        dateValues = [format(rangeStart, 'yyyy-MM-dd')];
        break;
      case 'afterDate':
        dateValues = [format(rangeEnd, 'yyyy-MM-dd')];
        break;
      default:
        return;
    }
  }

  innerCondition.push({
    member: innerRecord.tableField,
    values: dateValues,
    operator: multiDateOperators[operator],
  });
}

function calculateDateRange(innerRecord, innerCondition) {
  let today = new Date();
  let dateOperator = innerRecord.operator;
  let operator = innerRecord.dateFilterOperatorType;
  const multiDateOperators = {
    equals: 'inDateRange',
    notEquals: 'notInDateRange',
    beforeDate: 'beforeDate',
    afterDate: 'afterDate',
  };
  let dateValues = [];
  switch (true) {
    case dateOperator === DATE_OPERATORS?.TODAY:
      dateValues = [format(new Date(), 'yyyy-MM-dd')];
      break;
    case dateOperator === DATE_OPERATORS?.YESTERDAY:
      dateValues = [format(subDays(new Date(), 1), 'yyyy-MM-dd')];
      break;
    case dateOperator === DATE_OPERATORS?.TOMORROW:
      dateValues = [format(addDays(new Date(), 1), 'yyyy-MM-dd')];
      break;
    case dateOperator === DATE_OPERATORS?.THIS_WEEK: {
      // Get the first day of the current week (Sunday)
      const firstDay = new Date(
        today.setDate(today.getDate() - today.getDay())
      );
      // Get the last day of the current week (Saturday)
      const lastDay = new Date(
        today.setDate(today.getDate() - today.getDay() + 6)
      );

      switch (operator) {
        case 'equals':
          dateValues = [
            format(addDays(firstDay, 1), 'yyyy-MM-dd'),
            format(addDays(lastDay, 1), 'yyyy-MM-dd'),
          ];
          break;
        case 'notEquals':
          dateValues = [
            format(addDays(firstDay, 1), 'yyyy-MM-dd'),
            format(addDays(lastDay, 1), 'yyyy-MM-dd'),
          ];
          break;
        case 'beforeDate':
          dateValues = [format(addDays(firstDay, 1), 'yyyy-MM-dd')];
          break;
        case 'afterDate':
          dateValues = [format(addDays(lastDay, 1), 'yyyy-MM-dd')];
          break;
        default:
          dateValues = [];
          break;
      }
      break;
    }
    case dateOperator === DATE_OPERATORS?.THIS_MONTH: {
      const startDate = startOfMonth(today);
      const endDate = endOfMonth(today);
      switch (operator) {
        case 'equals':
          dateValues = [
            format(startDate, 'yyyy-MM-dd'),
            format(endDate, 'yyyy-MM-dd'),
          ];
          break;
        case 'notEquals':
          dateValues = [
            format(startDate, 'yyyy-MM-dd'),
            format(endDate, 'yyyy-MM-dd'),
          ];
          break;
        case 'beforeDate':
          dateValues = [format(startDate, 'yyyy-MM-dd')];
          break;
        case 'afterDate':
          dateValues = [format(endDate, 'yyyy-MM-dd')];
          break;
        default:
          dateValues = [];
          break;
      }
      break;
    }
    case dateOperator === DATE_OPERATORS?.THIS_YEAR: {
      const startYearDate = startOfYear(today);
      const endYearDate = endOfYear(today);
      switch (operator) {
        case 'equals':
          dateValues = [
            format(startYearDate, 'yyyy-MM-dd'),
            format(endYearDate, 'yyyy-MM-dd'),
          ];
          break;
        case 'notEquals':
          dateValues = [
            format(startYearDate, 'yyyy-MM-dd'),
            format(endYearDate, 'yyyy-MM-dd'),
          ];
          break;
        case 'beforeDate':
          dateValues = [format(startYearDate, 'yyyy-MM-dd')];
          break;
        case 'afterDate':
          dateValues = [format(endYearDate, 'yyyy-MM-dd')];
          break;
        default:
          dateValues = [];
          break;
      }
      break;
    }
    case dateOperator === DATE_OPERATORS?.LAST_YEAR: {
      const startLastYearDate = startOfYear(subYears(today, 1));
      const endLastYearDate = endOfYear(subYears(today, 1));
      switch (operator) {
        case 'equals':
          dateValues = [
            format(startLastYearDate, 'yyyy-MM-dd'),
            format(endLastYearDate, 'yyyy-MM-dd'),
          ];
          break;
        case 'notEquals':
          dateValues = [
            format(startLastYearDate, 'yyyy-MM-dd'),
            format(endLastYearDate, 'yyyy-MM-dd'),
          ];
          break;
        case 'beforeDate':
          dateValues = [format(startLastYearDate, 'yyyy-MM-dd')];
          break;
        case 'afterDate':
          dateValues = [format(endLastYearDate, 'yyyy-MM-dd')];
          break;
        default:
          dateValues = [];
          break;
      }
      break;
    }
    case dateOperator.startsWith('Last'): {
      let lastDays = dateOperator.split(' ')[1];
      if (isNaN(lastDays)) {
        if (lastDays === 'week') {
          const weekStart = startOfWeek(today);
          switch (operator) {
            case 'equals':
              dateValues = [
                format(subDays(weekStart, 6), 'yyyy-MM-dd'),
                format(weekStart, 'yyyy-MM-dd'),
              ];
              break;
            case 'notEquals':
              dateValues = [
                format(subDays(weekStart, 6), 'yyyy-MM-dd'),
                format(weekStart, 'yyyy-MM-dd'),
              ];
              break;
            case 'beforeDate':
              dateValues = [format(subDays(weekStart, 6), 'yyyy-MM-dd')];
              break;
            case 'afterDate':
              dateValues = [format(weekStart, 'yyyy-MM-dd')];
              break;
            default:
              dateValues = [];
              break;
          }
        } else if (lastDays === 'month') {
          const lastMonthStart = startOfMonth(subMonths(today, 1));
          const lastMonthEnd = endOfMonth(subMonths(today, 1));

          switch (operator) {
            case 'equals':
            case 'notEquals':
              dateValues = [
                format(lastMonthStart, 'yyyy-MM-dd'),
                format(lastMonthEnd, 'yyyy-MM-dd'),
              ];
              break;
            case 'beforeDate':
              dateValues = [format(lastMonthStart, 'yyyy-MM-dd')];
              break;
            case 'afterDate':
              dateValues = [format(lastMonthEnd, 'yyyy-MM-dd')];
              break;
            default:
              dateValues = [];
              break;
          }
        } else if (lastDays === 'quarter') {
          const monthQtrStart = startOfQuarter(today);
          switch (operator) {
            case 'equals':
              dateValues = [
                format(subDays(monthQtrStart, 90), 'yyyy-MM-dd'),
                format(subDays(monthQtrStart, 1), 'yyyy-MM-dd'),
              ];
              break;
            case 'notEquals':
              dateValues = [
                format(subDays(monthQtrStart, 90), 'yyyy-MM-dd'),
                format(subDays(monthQtrStart, 1), 'yyyy-MM-dd'),
              ];
              break;
            case 'beforeDate':
              dateValues = [format(subDays(monthQtrStart, 90), 'yyyy-MM-dd')];
              break;
            case 'afterDate':
              dateValues = [format(subDays(monthQtrStart, 1), 'yyyy-MM-dd')];
              break;
            default:
              dateValues = [];
              break;
          }
        } else if (lastDays === 'x') {
          const value = innerRecord?.value;
          switch (operator) {
            case 'equals':
              dateValues = [
                format(subDays(new Date(), value), 'yyyy-MM-dd'),
                format(new Date(), 'yyyy-MM-dd'),
              ];
              break;
            case 'notEquals':
              dateValues = [
                format(subDays(new Date(), value), 'yyyy-MM-dd'),
                format(new Date(), 'yyyy-MM-dd'),
              ];
              break;
            case 'beforeDate':
              dateValues = [format(subDays(new Date(), value), 'yyyy-MM-dd')];
              break;
            case 'afterDate':
              dateValues = [format(subDays(new Date(), value), 'yyyy-MM-dd')];
              break;
            default:
              dateValues = [];
              break;
          }
        }
      } else {
        switch (operator) {
          case 'equals':
            dateValues = [
              format(subDays(new Date(), lastDays), 'yyyy-MM-dd'),
              format(subDays(new Date(), 1), 'yyyy-MM-dd'),
            ];
            break;
          case 'notEquals':
            dateValues = [
              format(subDays(new Date(), lastDays), 'yyyy-MM-dd'),
              format(subDays(new Date(), 1), 'yyyy-MM-dd'),
            ];
            break;
          case 'beforeDate':
            dateValues = [format(subDays(new Date(), lastDays), 'yyyy-MM-dd')];
            break;
          case 'afterDate':
            dateValues = [format(subDays(new Date(), 1), 'yyyy-MM-dd')];
            break;
          default:
            dateValues = [];
            break;
        }
      }
      break;
    }

    case dateOperator === DATE_OPERATORS?.THIS_QUARTER: {
      const startQuarterDate = startOfQuarter(today);
      const endQuarterDate = endOfQuarter(today);
      switch (operator) {
        case 'equals':
          dateValues = [
            format(startQuarterDate, 'yyyy-MM-dd'),
            format(endQuarterDate, 'yyyy-MM-dd'),
          ];
          break;
        case 'notEquals':
          dateValues = [
            format(startQuarterDate, 'yyyy-MM-dd'),
            format(endQuarterDate, 'yyyy-MM-dd'),
          ];
          break;
        case 'beforeDate':
          dateValues = [format(startQuarterDate, 'yyyy-MM-dd')];
          break;
        case 'afterDate':
          dateValues = [format(endQuarterDate, 'yyyy-MM-dd')];
          break;
        default:
          dateValues = [];
          break;
      }
      break;
    }
    case dateOperator === DATE_OPERATORS?.TILL_TODAY:
      switch (operator) {
        case 'equals':
          dateValues = [
            format(subDays(new Date(), 100000), 'yyyy-MM-dd'),
            format(subDays(new Date(), 1), 'yyyy-MM-dd'),
          ];
          break;
        case 'notEquals':
          dateValues = [
            format(subDays(new Date(), 100000), 'yyyy-MM-dd'),
            format(subDays(new Date(), 1), 'yyyy-MM-dd'),
          ];
          break;
        case 'beforeDate':
          dateValues = [format(new Date(), 'yyyy-MM-dd')];
          break;
        case 'afterDate':
          dateValues = [format(new Date(), 'yyyy-MM-dd')];
          break;
        default:
          dateValues = [];
          break;
      }
      break;
    case dateOperator === DATE_OPERATORS?.FROM_365_DAYS_TO_NOW:
      switch (operator) {
        case 'equals':
          dateValues = [
            format(subDays(new Date(), 365), 'yyyy-MM-dd'),
            format(subDays(new Date(), 1), 'yyyy-MM-dd'),
          ];
          break;
        case 'notEquals':
          dateValues = [
            format(subDays(new Date(), 365), 'yyyy-MM-dd'),
            format(subDays(new Date(), 1), 'yyyy-MM-dd'),
          ];
          break;
        case 'beforeDate':
          dateValues = [format(subDays(new Date(), 365), 'yyyy-MM-dd')];
          break;
        case 'afterDate':
          dateValues = [format(subDays(new Date(), 1), 'yyyy-MM-dd')];
          break;
        default:
          dateValues = [];
          break;
      }
      break;
    case dateOperator.startsWith('Next'): {
      let nextDays = dateOperator.split(' ')[1];
      if (nextDays === 'week') {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const daysUntilSunday = (7 - dayOfWeek) % 7;
        const startDay = daysUntilSunday === 0 ? 7 : daysUntilSunday;
        switch (operator) {
          case 'equals':
          case 'notEquals':
            dateValues = [
              format(addDays(new Date(), startDay), 'yyyy-MM-dd'),
              format(addDays(new Date(), startDay + 6), 'yyyy-MM-dd'),
            ];
            break;
          case 'beforeDate':
            dateValues = [format(addDays(new Date(), startDay), 'yyyy-MM-dd')];
            break;
          case 'afterDate':
            dateValues = [
              format(addDays(new Date(), startDay + 6), 'yyyy-MM-dd'),
            ];
            break;
          default:
            dateValues = [];
            break;
        }
      } else if (nextDays === 'x') {
        const value = innerRecord?.value;
        switch (operator) {
          case 'equals':
            dateValues = [
              format(new Date(), 'yyyy-MM-dd'),
              format(addDays(new Date(), value), 'yyyy-MM-dd'),
            ];
            break;
          case 'notEquals':
            dateValues = [
              format(new Date(), 'yyyy-MM-dd'),
              format(addDays(new Date(), value), 'yyyy-MM-dd'),
            ];
            break;
          case 'beforeDate':
            dateValues = [format(addDays(new Date(), value), 'yyyy-MM-dd')];
            break;
          case 'afterDate':
            dateValues = [format(addDays(new Date(), value), 'yyyy-MM-dd')];
            break;
          default:
            dateValues = [];
            break;
        }
      } else {
        switch (operator) {
          case 'equals':
            dateValues = [
              format(addDays(new Date(), 1), 'yyyy-MM-dd'),
              format(addDays(new Date(), nextDays), 'yyyy-MM-dd'),
            ];
            break;
          case 'notEquals':
            dateValues = [
              format(addDays(new Date(), 1), 'yyyy-MM-dd'),
              format(addDays(new Date(), nextDays), 'yyyy-MM-dd'),
            ];
            break;
          case 'beforeDate':
            dateValues = [format(addDays(new Date(), 1), 'yyyy-MM-dd')];
            break;
          case 'afterDate':
            dateValues = [format(addDays(new Date(), nextDays), 'yyyy-MM-dd')];
            break;
          default:
            dateValues = [];
            break;
        }
      }
      break;
    }
    case dateOperator.startsWith('From now to'): {
      let withinDays = dateOperator.split(' ')[3];
      switch (operator) {
        case 'equals':
          dateValues = [
            format(new Date(), 'yyyy-MM-dd'),
            format(addDays(new Date(), withinDays), 'yyyy-MM-dd'),
          ];
          break;
        case 'notEquals':
          dateValues = [
            format(new Date(), 'yyyy-MM-dd'),
            format(addDays(new Date(), withinDays), 'yyyy-MM-dd'),
          ];
          break;
        case 'beforeDate':
          dateValues = [format(new Date(), 'yyyy-MM-dd')];
          break;
        case 'afterDate':
          dateValues = [format(addDays(new Date(), withinDays), 'yyyy-MM-dd')];
          break;
        default:
          dateValues = [];
          break;
      }
      break;
    }
    default:
      break;
  }
  innerCondition.push({
    member: innerRecord.tableField,
    values: dateValues,
    operator: [
      DATE_OPERATORS?.TODAY,
      DATE_OPERATORS?.YESTERDAY,
      DATE_OPERATORS?.TOMORROW,
    ].includes(dateOperator)
      ? operator
      : multiDateOperators[operator],
  });
}

function widgetTypeFilter(query, formattedQuery, filterDetails) {
  if ('user-filter' == query.widget_filter_type && filterDetails) {
    formattedQuery.filters.push(filterDetails.userFilterCondition);
  } else if ('company-filter' == query.widget_filter_type && filterDetails) {
    formattedQuery.filters.push(filterDetails.companyFilterCondition);
  } else if ('assigned-to-self' == query.widget_filter_type && filterDetails) {
    formattedQuery.filters.push({
      member: 'UserMeta.user_id',
      values: [getLoginUserId()],
      operator: 'equals',
    });
  } else if (
    'company-and-assigned' == query.widget_filter_type &&
    filterDetails
  ) {
    formattedQuery.filters.push({
      member: 'UserMeta.user_id',
      values:
        filterDetails?.companyUsers?.length > 0
          ? filterDetails?.companyUsers
          : ['0'],
      operator: 'equals',
    });
  } else if (
    'user-and-assigned-to-self' == query.widget_filter_type &&
    filterDetails
  ) {
    const userAndAssignToSelfFilter = {
      or: [
        {
          member: 'UserMeta.user_id',
          values: [getLoginUserId()],
          operator: 'equals',
        },
      ],
    };
    if (filterDetails.userFilterCondition) {
      userAndAssignToSelfFilter.or.push(filterDetails.userFilterCondition);
    }
    formattedQuery.filters.push(userAndAssignToSelfFilter);
  } else if (
    'opportunity-owner' == query?.widget_filter_type &&
    filterDetails
  ) {
    const opportunityAssignedFilter = {
      member: 'Opportunity.owner_user_id',
      values: [getLoginUserId()],
      operator: 'equals',
    };
    formattedQuery.filters.push(opportunityAssignedFilter);
  }
}

function getColumnFilters(tablePagination, cubeResponse, formattedQuery) {
  if (tablePagination.filters && Object.keys(tablePagination.filters)?.length) {
    const tempFilters = [];
    const tempTimeDimension = [];
    Object.keys(tablePagination.filters).forEach(key => {
      if (cubeResponse?.annotation?.dimensions?.[key]?.type == 'time') {
        if (tablePagination?.filters?.[key]) {
          tablePagination.filters?.[key].constraints.forEach(filterKey => {
            if (filterKey?.value && filterKey.value?.length) {
              tempTimeDimension.push({
                dimension: key,
                dateRange: [
                  moment(filterKey.value[0]).format('Y-M-D'),
                  moment(filterKey.value[1]).format('Y-M-D'),
                ],
              });
            }
          });
        }
      } else if (tablePagination.filters?.[key]) {
        tablePagination.filters?.[key].constraints.forEach(filterKey => {
          if (filterKey.value) {
            tempFilters.push({
              member: key,
              values: [filterKey.value?.toString()],
              operator: filterKey.matchMode,
            });
          }
        });
      }
    });
    if (tempFilters.length) {
      formattedQuery.filters.push(...tempFilters);
    }
    if (tempTimeDimension) {
      formattedQuery.timeDimensions.push(...tempTimeDimension);
    }
  }
}

export const routingWidgetDefaultCondition = () => {
  return {
    member: 'Project.status',
    values: ['Cancelled', 'Closed', 'Completed', 'Refunded'],
    operator: 'notEquals',
  };
};
